import { BomItemController } from 'controllers/Project/BomItemController'
import { fetchProject } from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { newProjectActions } from 'features/BillOfMaterials/store/newProjectReducer'
import _ from 'lodash'
import { PartTypeRow } from 'model/Project/BoMItemRow'
import { Action } from 'redux'
import { UIActions } from 'store/UI/UIActions'
import { AppState } from '..'
import {
  KeywordDto,
  MaterialSelectorDto,
  MaterialSummaryDto,
  RowDto,
} from '../../services/APIs/InternalAPI/internal-api.contracts'
import { APICall, ErrorMessage, ShowException } from '../Application/appActions'
import { APICallArgs } from '../Application/appState'
import { getContext } from '../getContext'

export enum MaterialActionsConsts {
  OpenMaterialModal = 'material/openMaterialModal',
  SaveChanges = 'material/SaveAndCloseMaterialModalAction',
  CloseWithoutSave = 'material/CloseWithoutSave',
  SetTokens = 'material/SetTokens',
  SetPossibleTokensAndMaterials = 'material/SetPossibleTokensAndMaterials',
  SetMaterial = 'material/SetRowMaterial',
  OpenModalRemnantStrategy = 'material/OpenModalRemnantStrategy',
  CloseModalRemnantStrategy = 'material/CloseModalRemnantStrategy',
  CopyMaterialToClipboard = 'material/Copy',
  PasteFromClipboard = 'material/Paste',
  CleanSelectedMaterial = 'material/CleanSelectedMaterial',
  SetActiveRows = 'material/SetActiveRows',
  SetIsLoading = 'material/SetIsLoading',
  Set3dModelPath = 'material/Set3dModel',
}

interface OpenMaterialAction extends Action {
  type: MaterialActionsConsts.OpenMaterialModal
  activeRows: PartTypeRow[]
  currentElementId: string
}

interface SaveAndCloseMaterialModalAction extends Action {
  type: MaterialActionsConsts.SaveChanges
}

interface CloseWithoutSaveModalAction extends Action {
  type: MaterialActionsConsts.CloseWithoutSave
}

interface SetMaterial extends Action {
  type: MaterialActionsConsts.SetMaterial
  material: MaterialSummaryDto
}

interface SetPossibleTokensAndMaterials extends Action {
  type: MaterialActionsConsts.SetPossibleTokensAndMaterials
  tokensAndMaterials: MaterialSelectorDto
  selectedMaterial: MaterialSummaryDto
}

interface SetTokens extends Action {
  type: MaterialActionsConsts.SetTokens
  selectedTokens: Record<string, KeywordDto[]>
}

interface CopyMaterialToClipboard extends Action {
  type: MaterialActionsConsts.CopyMaterialToClipboard
  materialSummary: MaterialSummaryDto
}

interface PasteMaterialFromClipboard extends Action {
  type: MaterialActionsConsts.PasteFromClipboard
  rowIds: string[]
}

interface OpenEditRemnantStrategyModal extends Action {
  type: MaterialActionsConsts.OpenModalRemnantStrategy
  materialId: string
  resourceId: string
  projectId: string
}

interface CloseModalRemnantStrategyModal extends Action {
  type: MaterialActionsConsts.CloseModalRemnantStrategy
}

interface CleanSelectedMaterial extends Action {
  type: MaterialActionsConsts.CleanSelectedMaterial
}

interface SetActiveRows extends Action {
  type: MaterialActionsConsts.SetActiveRows
  activeRows: RowDto
}

interface SetIsLoading extends Action {
  type: MaterialActionsConsts.SetIsLoading
  isLoading: boolean
}

interface Set3DModelPath extends Action {
  type: MaterialActionsConsts.Set3dModelPath
  rowId: string
  threeDModelPath: string
}

export type MaterialActionsTypes =
  | OpenMaterialAction
  | SaveAndCloseMaterialModalAction
  | CloseWithoutSaveModalAction
  | SetMaterial
  | SetTokens
  | SetPossibleTokensAndMaterials
  | CopyMaterialToClipboard
  | PasteMaterialFromClipboard
  | OpenEditRemnantStrategyModal
  | CloseModalRemnantStrategyModal
  | CleanSelectedMaterial
  | SetActiveRows
  | SetIsLoading
  | Set3DModelPath

const debouncedChangeTokens = _.debounce(
  (dispatch, selectedTokens: KeywordDto[]) => {
    dispatch(MaterialActions.getTokensAndMaterials(selectedTokens))
  },
  420,
  { leading: false, trailing: true }
)

export const MaterialActions = {
  getTokensAndMaterials:
    (selectedTokens: KeywordDto[]) => (dispatch, getState: () => AppState) => {
      const { partyId: organizationId, projectId } = getContext(getState)
      let materialUrl = `api/parties/${organizationId}`

      if (getState().materials.activeRows[0].id === '000-000-000') {
        materialUrl += '/materials'
      } else materialUrl += `/projects/${projectId}/materials`

      const workingSteps = getState().materials.activeRows[0].workingSteps

      const bomItemWorkingSteps = workingSteps
        ? workingSteps.map((x) => x.primaryWorkingStep)
        : []

      dispatch(
        APICall(
          new APICallArgs({
            label: 'loading-materials',
            configuration: {
              url: materialUrl,
              method: 'GET',
              data: {
                selectedTokens: selectedTokens.map(
                  (token) => token.originalKeyword
                ),
                bomItemWorkingSteps,
              },
              onSuccess: (data: MaterialSelectorDto) => {
                dispatch({
                  type: MaterialActionsConsts.SetPossibleTokensAndMaterials,
                  tokensAndMaterials: data,
                  selectedMaterial:
                    getState().materials.activeRows[0].materialSummary,
                })
              },
              onError: (error: string) => {
                ErrorMessage('material selector', error)
              },
            },
          })
        )
      )
    },
  initMaterialSelector:
    (rowIds: string[], currentElementId?: string) =>
    (dispatch, getState: () => AppState) => {
      const rows: RowDto[] = []

      if (rowIds[0] === '000-000-000') {
        rows.push({
          id: '000-000-000',
          materialSummary: {},
        })
      } else {
        rowIds.forEach((x) => {
          rows.push(getState().project.flatView[x] as RowDto)
        })
      }

      dispatch({
        type: MaterialActionsConsts.OpenMaterialModal,
        activeRows: rows,
        currentElementId: currentElementId,
      })

      const currentRow = rows[0]

      setTimeout(
        () =>
          dispatch(
            MaterialActions.getTokensAndMaterials(
              Object.values(currentRow.materialSummary.tokens).flat()
            )
          ),
        250
      )
    },
  changeTokens: (selectedTokens: KeywordDto[]) => (dispatch) => {
    dispatch({
      type: MaterialActionsConsts.SetTokens,
      selectedTokens: selectedTokens,
    })

    debouncedChangeTokens(dispatch, selectedTokens)
  },
  selectMaterial: (material: MaterialSummaryDto) => {
    // the material will be updated on materialSelector state first
    return {
      type: MaterialActionsConsts.SetMaterial,
      material: material,
    }
  },
  saveSelectedTokensAndMaterials:
    () => async (dispatch, getState: () => AppState) => {
      const { partyId: organizationId, projectId } = getContext(getState)

      const bomItemController = new BomItemController(organizationId, projectId)

      const materialsState = getState().materials

      try {
        await bomItemController.SetKeywordsOrArticleToParts({
          rowId: materialsState.activeRows.map((x) => x.id),
          keywords: materialsState.materialSelectorUI.selectedTokens,
          articleId: materialsState.materialSelectorUI.selectedMaterial.id,
          articleDescription:
            materialsState.materialSelectorUI.selectedMaterial.description,
          headerId: materialsState.currentElementId,
        })

        dispatch(fetchProject({ projectId: projectId }))
        dispatch(UIActions.CloseModal())

        dispatch(
          newProjectActions.setBomItemInSearch(
            getState().materials.activeRows[0].id
          )
        )
      } catch (err) {
        ShowException('project', err)
        dispatch(fetchProject)
      }

      // dispatch(UIActions.CloseModal())
      // dispatch(
      //   newProjectActions.setBomItemInSearch(
      //     getState().materials.activeRows[0].id
      //   )
      // )
      // requestIdleCallback(async () => {
      //   try {
      //     const materialsState = getState().materials

      //     await bomItemController.SetKeywordsOrArticleToParts({
      //       rowId: materialsState.activeRows.map((x) => x.id),
      //       keywords: materialsState.materialSelectorUI.selectedTokens,
      //       articleId: materialsState.materialSelectorUI.selectedMaterial.id,
      //       articleDescription:
      //         materialsState.materialSelectorUI.selectedMaterial.description,
      //       headerId: headerId,
      //     })
      //   } catch (err) {
      //     ShowException('project', err)
      //     dispatch(UIActions.CloseModal())
      //     dispatch(fetchProject)
      //   }
      // })
    },
  closeModal: () => {
    return {
      type: MaterialActionsConsts.CloseWithoutSave,
    }
  },
  openModalRemnantStrategy: (
    projectId: string,
    resourceId: string,
    materialId: string
  ) => {
    return {
      type: MaterialActionsConsts.OpenModalRemnantStrategy,
      materialId,
      resourceId,
      projectId,
    }
  },

  closeModalRemnantStrategy: () => ({
    type: MaterialActionsConsts.CloseModalRemnantStrategy,
  }),

  copyMaterial: (materialSummary: MaterialSummaryDto) => {
    return {
      type: MaterialActionsConsts.CopyMaterialToClipboard,
      materialSummary: materialSummary,
    }
  },
  pasteMaterial:
    (rowIds: string[]) => async (dispatch, getState: () => AppState) => {
      // need to get the dimensions from the target row!!!
      const materialSummary = getState().materials.materialSelectorClipboard

      if (!materialSummary) {
        return
      }

      // const { organizationId, projectId } = getContext(getState)

      rowIds.forEach((row) => {
        // const currentRow = getState().project.flatView[row] as RowDto
        // const newMaterialSummary = {
        //   ...currentRow.materialSummary,
        //   tokens: {
        //     ...materialSummary.tokens,
        //   },
        // }

        dispatch(
          newProjectActions.setBomItemProperties({
            id: row,
            materialSummary,
            replacementBehaviour: 'fullReplace',
          })
        )
        // dispatch(
        //   saveBomItemMaterials({
        //     bomItemId: row,
        //     material: materialSummary,
        //   })
        // )
        // dispatch(oldProjectActions.AddRowToMaterialHeader(row, materialSummary))
      })

      // try {
      //   // const bomItemController = new BomItemController(organizationId, projectId)
      //   // const materialController = new MaterialsController(
      //   //   organizationId,
      //   //   dispatch
      //   // )

      //   // await materialController.AddKeywordsToRows(projectId, {
      //   //   rowIds: rowIds,
      //   //   keywords: Object.values(materialSummary.tokens).flat(),
      //   // })
      // } catch (err) {
      //   dispatch(fetchProject({projectId: projectId}))
      //   throw err
      // }

      // dispatch(projectActions.RemoveAllCalculatedFields())

      // dispatch(
      //   APICall(
      //     new APICallArgs({
      //       label: 'saving-tokens-and-materials',
      //       configuration: {
      //         url: `api/parties/${organizationId}/projects/${projectId}/materials`,
      //         method: 'PUT',
      //         data: {
      //           rowIds: rowIds,
      //           materialSummary: materialSummary,
      //         },
      //         onSuccess: () => {
      //           // SuccessMessage('project', 'project saved')
      //           // dispatch(
      //           //   projectActions.LoadProjectInfo({
      //           //     projectId,
      //           //     shouldSubscribleToUpdates: false,
      //           //   })
      //           // )
      //         },
      //         onError: (message: string) => {
      //           ErrorMessage('project', message)
      //           dispatch(
      //             projectActions.LoadProjectInfo({
      //               projectId,
      //               shouldSubscribleToUpdates: false,
      //             })
      //           )
      //         },
      //       },
      //     })
      //   )
      // )
    },
  flipModel:
    (rowId: string, reset: boolean) => (dispatch, getState: () => AppState) => {
      const { partyId: organizationId, projectId } = getContext(getState)

      dispatch(
        APICall(
          new APICallArgs({
            label: 'flip-model',
            configuration: {
              url: `api/parties/${organizationId}/projects/${projectId}/items/${rowId}/flip3dModel?reset=${
                reset || false
              }`,
              method: 'PUT',
              onSuccess: (fileName: string) => {
                dispatch({
                  type: MaterialActionsConsts.Set3dModelPath,
                  rowId: rowId,
                  threeDModelPath: fileName,
                })
              },
              onError: (message: string) => {
                ErrorMessage('project', message)
              },
            },
          })
        )
      )
    },
  rotateModel: (rowId: string) => (dispatch, getState: () => AppState) => {
    const { partyId: organizationId, projectId } = getContext(getState)

    dispatch(
      APICall({
        label: 'rotate-model',
        configuration: {
          url: `api/parties/${organizationId}/projects/${projectId}/items/${rowId}/rotate3dModel`,
          method: 'PUT',
          onSuccess: (fileName: string) => {
            dispatch({
              type: MaterialActionsConsts.Set3dModelPath,
              rowId: rowId,
              threeDModelPath: fileName,
            })
          },
          onError: (message: string) => {
            ErrorMessage('project', message)
          },
        },
      })
    )
  },
}
